import { RouterRootState } from "connected-react-router";
import React, {FC, useEffect, useState} from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useDialog } from "../../../components/dialog/DialogContext";
import ItemContentTabTwo from "../../../components/item-content-two/item-content-tab_two";
import { Topic } from "../../../components/item-content-two/Topic";
import SimpleItemCard from "../../../components/item-content/SimpleItemCard";
import SimpleItemDetails from "../../../components/item-content/SimpleItemDetails";
import { toast } from "react-toastify";
import { useLocale } from "../../../contexts/localeContext";
import { TopicContextProvider } from "../../../contexts/topic/TopicContextProvider";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { BreadcrumbLink } from "../../../models/BreadcrumbLink";
import EnrolmentColumns from "../../../models/ColumnDefinitions/EnrolmentColumns";
import { ContentWrapper } from "../../../models/ContentWrapper";
import { Department as DepartmentModel } from "../../../models/Department";
import { TabSummary } from "../../../models/TabSummary";
import { getDepartmentById, getDepartmentNamesWithPagination, storeDepartment, assignMembersToDepartment } from "../../../services/departmentService";
import { assignUnassignMembers, getEnrolmentNamesWithPagination, getEnrolments } from "../../../services/enrolmentService";
import { getErrorMessage, showLoadItemError } from "../../../services/utilities/ServiceError";
import { IAppState } from "../../../store/types";
import { appendFilter } from "../../../utilities/filters";
import { Lookup } from "../../../models/Lookup";
import { hideColumns } from "../../../models/utilities/columnHelper";
import EnrolmentSearch from "../../../models/search/EnrolmentSearch";
import EnrolmentColumn from "../../../models/ColumnDefinitions/EnrolmentColumns";
import { MultiSelectOption } from "../../../models/MultiSelectOption";
import LookupTypes from "../../../models/LookupTypes";
import CategorySearch from "../../../models/search/CategorySearch";
import CompanySearch from "../../../models/search/CompanySearch";
import DepartmentSearch from "../../../models/search/DepartmentSearch";
import SiteSearch from "../../../models/search/SiteSearch";
import { getCategoryNameWithPagination } from "../../../services/categoryService";
import { getCompanyNamesWithPagination } from "../../../services/companyService";
import { getSiteNamesWithPagination } from "../../../services/siteService";
import {ListQuery} from "../../../models/ListQuery";
import AssignModalTwo from "@/components/assignWizard/AssignModal";
import AssignedTab from "@/components/assignWizard/AssignedTab";
import ManageButton from "@/components/assignWizard/ManageButton";
import { PaginationModel } from "@/models/Pagination";
import { Name } from "@/models/Name";
import { Enrolment } from "@/models/Enrolment";

const getIdFromPath = (path: string) => {
    const splitPath = path.split("/");
    return splitPath[splitPath.length - 1];
};

const allItemsPath = "/access-control/enrolment/departments";
const itemPath = "/access-control/enrolment/department/";
const departmentName = "deptName";
// <--

const initialItem: DepartmentModel = {
    id: 0,
    name: "",
    site: "",
    siteId: 0,
    default: undefined,
    enrolments: 0,
};

const Department: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        {
            label: translate.byKey(TranslationKeys.members),
            badge: 0,
            showInitial: true,
            isActive: false,
        },
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const site = useSelector((state: IAppState) => state.site.site);
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<DepartmentModel>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs);
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const dialog = useDialog();

    const lookup: Lookup[] = [
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.company,
            values: [],
            dataMethod: getCompanyNamesWithPagination,
            searchAccessor: CompanySearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.department,
            values: [],
            dataMethod: getDepartmentNamesWithPagination,
            searchAccessor: DepartmentSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.profile_id,
            values: [],
            dataMethod: getCategoryNameWithPagination,
            searchAccessor: CategorySearch.find(item => item.accessor === "name")?.searchOnName
        }
    ]

    const getAssignedMembers = (listParameters) => {
        return getEnrolments(translate, {
            ...listParameters,
            filters:itemId ? appendFilter(
                itemId,
                "Department",
                "department.id",
                listParameters.filters??[]
            ) : listParameters.filters
        });
    };

    const tabContentList: ContentWrapper[] = [
        {
            content: <AssignedTab
                key={`AssignedMembersForDepartment${itemId}`}
                title={translate.byKey(TranslationKeys.assignedMembers)}
                column={hideColumns(EnrolmentColumns, [departmentName])}
                searchColumn={EnrolmentSearch}
                id={itemId}
                dataMethod={getAssignedMembers}
                lookup={lookup}>
                <ManageButton onClick={() => setShowAddModal(true)}/>
            </AssignedTab>
        },
    ];

    const topic: Topic = {
        accessor: "deptName",
        title: translate.byKey(TranslationKeys.departments),
        allItemsPath: allItemsPath,
        itemPath: itemPath,
    };

    /**
     * Fetch all the required data on a re-render
     */
    const reloadPage = async () => {
        try {
            let newCurrentDepartment: any;
            if (itemId) {
                newCurrentDepartment = await getDepartmentById(+itemId);

                setSelectedItem({label: newCurrentDepartment.name, value: newCurrentDepartment.id.toString(), isDefault: newCurrentDepartment.isDefault})
                setCurrentItem(newCurrentDepartment);
                setTabs(
                    updateTabBadgeTotal(
                        0,
                        newCurrentDepartment?.enrolments,
                        tabs
                    )
                );
            } else {
                setCurrentItem(initialItem);
                setTabs([initialTabs[0]]); // Reset to initial tabs (i.e. on create new)
            }

            setBreadcrumb([
                {
                    value: "/access-control/enrolment",
                    displayName: translate.byKey(TranslationKeys.enrolments),
                },
                {
                    value: allItemsPath,
                    displayName: translate.byKey(TranslationKeys.departments),
                },
                {
                    value: itemPath + `${itemId}`,
                    displayName: `${newCurrentDepartment?.name ?? ""}`,
                },
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => {
                history.push(allItemsPath);
            });
        }
    };

    /**
     * Save details when the form is submitted
     * @param values
     */
    const saveDepartment = (values) => {
        const department: DepartmentModel = {
            ...currentItem,
            name: values.name,
            siteId: itemId ? values.siteId : site.value,
            site: itemId ? values.site : site.label,
            id: +itemId,
        };
        const storeMethod = storeDepartment;
        const isNew = itemId ? +itemId < 0 : true;
        saveItem(setBusy, storeMethod, department, isNew, setItemId, history);
    };

    // <--

    useEffect(() => {
        setItemId(pageId);
    }, [pageId]);

    useEffect(() => {
        reloadPage();
    }, [itemId]);

    /**
     *
     * @returns Inner content block
     */
    const getTabContent = (index?: number) => {
        // Only return the tab content if the tab is active (clicked an existing record)
        if (itemId && tabs[index ?? 0].isActive) {
            const selectedTabContent = tabContentList[index ?? 0];
            return selectedTabContent.content;
        }
    };

    const updateTabBadgeTotal = (tabIndex: number, totalCount: number, currentTabs: TabSummary[]) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        newTabs[tabIndex].isActive = true; // Set tab to active so we can show it
        return newTabs;
    };

    const saveItem = (
        setBusy,
        storeMethod,
        item,
        createNewItem,
        setItemId,
        history
    ) => {
        setBusy(true);
        storeMethod(item, createNewItem)
            .then(async (value) => {
                setCurrentItem(item);
                setItemId(value.id);
                history.push(`${itemPath}${value.id}`);
                toast.success(
                    translate.byKey(TranslationKeys.departmentSaved)
                );
            })
            .catch((failure) => {
                dialog.pushError({
                    title: translate.byKey(TranslationKeys.error),
                    message: getErrorMessage(failure),
                });
            })
            .finally(() => {
                setBusy(false);
            });
    };

    const getAssignedItems = async (setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setSelectedRows: React.Dispatch<React.SetStateAction<any[]>>, setOriginalSelectedIds: React.Dispatch<React.SetStateAction<string[]>>, setOriginalSelectedRows: React.Dispatch<React.SetStateAction<any[]>>) => {
        try {
            let pageNumber = 1, pagination: PaginationModel, ids: string[] = [], rows: Enrolment[] = []
            do {
                const enrolments = await getEnrolments(translate, { filters: itemId ? appendFilter(itemId, "Department", "department.id", []) : [], pageSize: 100, goToPage: pageNumber });
                ids = ids.concat(enrolments.rows.map(enrolment => enrolment.id.toString()))
                rows = rows.concat(enrolments.rows);
                pagination = enrolments.pagination;
                pageNumber++;
            } while (pageNumber <= pagination.totalPages);
            setSelectedIds(ids);
            setSelectedRows(rows)
            setOriginalSelectedIds(ids);
            setOriginalSelectedRows(rows);
        } catch (error) {
            toast.warn(getErrorMessage(error));
        }
    }

    const handleSubmit = (toBeAdded: string[], toBeRemoved: string[]) => {
        setBusy(true);
        assignUnassignMembers(toBeAdded, toBeRemoved, itemId.toString(), "department").then(() => {
            reloadPage();
            toast.success(translate.byKey(TranslationKeys.success));
        }).catch((error) => {
            dialog.pushError({
                title: translate.byKey(TranslationKeys.error),
                message: getErrorMessage(error),
            });
        })
        .finally(() => {
            setBusy(false);
        });
    };

    return (
        <div className="flex h-full">
            <TopicContextProvider
                dataMethod={getDepartmentNamesWithPagination}
                topic={topic}
                itemId={itemId}
                selectedItem={selectedItem}>
                <ItemContentTabTwo>
                    <SimpleItemDetails
                        breadcrumb={breadcrumb}
                        busy={busy}
                        getTabContent={getTabContent}
                        itemProfile={currentItem}
                        onBack={() => history.push(allItemsPath)}
                        saveDetails={saveDepartment}
                        tabs={tabs}
                        initialValues={currentItem}
                        itemPath={itemPath}
                        children={<SimpleItemCard />}/>
                </ItemContentTabTwo>
            </TopicContextProvider>
            <AssignModalTwo
                isOpen={showAddModal}
                dataMethod={(listParameter?:ListQuery) => getEnrolments(translate, listParameter)}
                lookup={lookup}
                onClose={() => setShowAddModal(false)}
                modalTitle={translate.byKey(TranslationKeys.assignMembers)}
                tableColumns={hideColumns(EnrolmentColumn, ["totalTags", "category"])}
                searchColumns={EnrolmentSearch}
                assigningTitle={translate.byKey(TranslationKeys.members)}
                getAssignedItems={getAssignedItems}
                handleSubmit={handleSubmit}/>
        </div>
    );
};

export default Department;
